import Button from '@creator/ui/components/Button/ButtonV2';
import Text from '@creator/ui/components/Text/Text';
import { cn } from '@creator/ui/utils/ui';
import AdvertisingSlot from '@src/components/Ads/AdvertisingSlot';
import EmptyState from '@src/components/Post/EmptyState';
import PostCard from '@src/components/Post/PostCard/PostCard';
import { shouldDisplayAds } from '@src/model/config/helpers';
import { getTokenIcon } from '@src/model/token/helpers';
import { usePaginatedPosts } from '@src/store/posts/hooks/usePaginatedPosts';
import { isMobile, isTablet } from '@src/utils/utils';
import type { FC } from 'react';
import { Fragment, memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface PartnerPostsProps {
    tokenName: string;
    flairId?: string;
    className?: string;
    excludePostId?: string;
}

function getInitialDisplayLimit() {
    if (isTablet()) return 4;
    if (isMobile()) return 5;
    return 7;
}

function getLoadMoreBacthSize() {
    if (isTablet()) return 6;
    if (isMobile()) return 5;
    return 9;
}

const PartnerPosts: FC<PartnerPostsProps> = props => {
    const { excludePostId, tokenName, flairId, className = '' } = props;
    const { postIds, loadMore, hasMore, loadStatus } = usePaginatedPosts({ type: 'partner', tokenName, flairId, fetchLimit: 20, includeMyReactions: true });
    const { postIds: pinnedPostsIds, loadMore: loadPinnedPosts } = usePaginatedPosts({ type: 'pinnedPartner', tokenName, includeMyReactions: true, flairId });

    const initialDisplayLimit = shouldDisplayAds(tokenName) ? getInitialDisplayLimit() : getInitialDisplayLimit() + 2;
    const batchSize = getLoadMoreBacthSize();
    const { t } = useTranslation();

    // State to control how many posts (besides the main one) are displayed
    const [displayLimit, setDisplayLimit] = useState<number>(initialDisplayLimit);

    // --- Combine Posts ---
    const combinedPosts = useMemo(() => {
        return Array.from(new Set([...pinnedPostsIds, ...postIds]));
    }, [postIds, pinnedPostsIds]);

    const remainingPosts = useMemo(() => {
        return combinedPosts.length > 0 ? combinedPosts.filter(id => id !== excludePostId) : [];
    }, [combinedPosts, excludePostId]);

    const postsToDisplay = useMemo(() => remainingPosts.slice(0, displayLimit), [remainingPosts, displayLimit]);

    useEffect(() => {
        if (pinnedPostsIds.length > 0) return;
        loadPinnedPosts();
    }, [tokenName, flairId]);

    useEffect(() => {
        if (postIds.length > 0) return;
        if (loadStatus === 'loading') return;
        loadMore();
    }, [flairId]);

    const handleLoadMore = () => {
        const newLimit = displayLimit + batchSize;
        if (newLimit > remainingPosts.length && hasMore && loadStatus !== 'loading') loadMore();
        setDisplayLimit(newLimit);
    };

    function renderLoadMoreButton() {
        const canShowMoreLocally = displayLimit < remainingPosts.length;
        const showButton = canShowMoreLocally || hasMore;

        if (!showButton) return null;

        return (
            <Button id="load-more-stories" isLoading={loadStatus === 'loading'} size="md" className="w-full" onClick={handleLoadMore}>
                <Text>{t('postList.partner.loadMoreButton')}</Text>
            </Button>
        );
    }

    // Renders a single post card in the grid
    function renderGridPost(postId: string, index: number) {
        const displayIndex = index + 1;
        const isMock = postId.includes('MOCK');
        return (
            <Fragment key={postId}>
                <PostCard
                    isLoading={isMock}
                    fallbackImage={isMock ? undefined : getTokenIcon(tokenName)}
                    tokenName={tokenName}
                    className={cn('bg-transparent dark:bg-transparent')}
                    postId={postId}
                />
                {shouldDisplayAds(tokenName) && isTablet() && displayIndex === 3 && <div className="hidden row-span-2 md:block lg:hidden">{renderInGridAd()}</div>}
                {shouldDisplayAds(tokenName) && !isMobile() && displayIndex === 5 && <div className="hidden row-span-2 lg:block">{renderInGridAd()}</div>}
            </Fragment>
        );
    }

    function renderEmptyState() {
        if (loadStatus !== 'success' || remainingPosts.length > 0) return null;
        return <EmptyState context="post" type="partner" />;
    }

    function renderPostsGrid() {
        const posts = loadStatus === 'success' ? postsToDisplay : postsToDisplay.concat(Array.from({ length: batchSize }, (v, i) => `MOCK-${i}`));
        return <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">{posts.map(renderGridPost)}</div>;
    }

    function renderInGridAd() {
        if (postsToDisplay.length === 0) return null;
        return <AdvertisingSlot divIdSufix={`partner-posts-${tokenName}`} location="mid_right" tokenName={tokenName} />;
    }

    return (
        <div id="partner-posts" className={cn('space-y-4 pb-4 lg:p-4', className)}>
            {renderEmptyState()}
            {renderPostsGrid()}
            {renderLoadMoreButton()}
        </div>
    );
};

export default memo(PartnerPosts);
